import React, { useState, useEffect } from 'react';
import { Calendar, Clock, User, AlertCircle, RotateCcw, Trash2 } from 'lucide-react';
import EnhancedModal, { ModalHeader, ModalBody, ModalFooter, ModalButton } from './EnhancedModal';
import { useTheme } from '../contexts/ThemeContext';

const EnhancedEditAppointmentModal = ({ appointment, employees, clients, onSave, onClose, onDelete, canDelete = true }) => {
    const { theme } = useTheme();

    // Clone appointment data for local editing
    const [formData, setFormData] = useState(() => ({
        ...appointment,
        // Ensure date is a Date object
        date: appointment.date instanceof Date ? appointment.date : new Date(appointment.date),
        // Ensure defaults
        startTime: appointment.startTime || '09:00',
        endTime: appointment.endTime || '10:00',
    }));

    // Update local state when prop changes
    useEffect(() => {
        setFormData({
            ...appointment,
            date: appointment.date instanceof Date ? appointment.date : new Date(appointment.date),
        });
    }, [appointment]);

    const handleChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleSave = () => {
        onSave(formData);
    };

    const handleDelete = () => {
        if (window.confirm("Êtes-vous sûr de vouloir supprimer ce rendez-vous ?")) {
            onDelete(formData.id);
        }
    };

    // Helper for array toggle (recurrence days)
    const toggleRecurrenceDay = (dayIndex) => {
        setFormData(prev => {
            const currentDays = prev.recurrenceDay || [];
            const newDays = currentDays.includes(dayIndex)
                ? currentDays.filter(d => d !== dayIndex)
                : [...currentDays, dayIndex].sort();
            return { ...prev, recurrenceDay: newDays };
        });
    };

    return (
        <EnhancedModal onClose={onClose} size="lg">
            <ModalHeader>
                Modifier le rendez-vous
                {formData.recurrenceSeriesId && (
                    <div className="flex items-center gap-2 mt-2 text-sm text-blue-600 dark:text-blue-400">
                        <RotateCcw size={14} />
                        <span>Rendez-vous récurrent</span>
                    </div>
                )}
            </ModalHeader>
            <ModalBody>
                <div className="space-y-4">
                    {/* Date */}
                    <div className="space-y-2">
                        <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
                            <Calendar className="mr-2 text-blue-500" size={16} />
                            Date du rendez-vous
                        </label>
                        <input
                            type="date"
                            value={formData.date instanceof Date && !isNaN(formData.date)
                                ? formData.date.toISOString().split('T')[0]
                                : ''}
                            onChange={(e) => {
                                const newDate = new Date(e.target.value);
                                if (!isNaN(newDate.getTime())) {
                                    handleChange('date', newDate);
                                }
                            }}
                            className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        />
                    </div>

                    {/* Horaires */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
                                <Clock className="mr-2 text-blue-500" size={16} />
                                Heure de début
                            </label>
                            <input
                                type="time"
                                value={formData.startTime}
                                onChange={(e) => handleChange('startTime', e.target.value)}
                                className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                            />
                        </div>

                        <div className="space-y-2">
                            <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
                                <Clock className="mr-2 text-blue-500" size={16} />
                                Heure de fin
                            </label>
                            <input
                                type="time"
                                value={formData.endTime}
                                onChange={(e) => handleChange('endTime', e.target.value)}
                                className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                            />
                        </div>
                    </div>

                    {/* Bénéficiaire */}
                    <div className="space-y-2">
                        <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
                            <User className="mr-2 text-blue-500" size={16} />
                            Bénéficiaire
                        </label>
                        <select
                            value={formData.clientId}
                            onChange={(e) => handleChange('clientId', e.target.value)}
                            className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        >
                            <option value="">Sélectionner un bénéficiaire...</option>
                            {clients.map(client => (
                                <option key={client.id} value={client.id}>
                                    {client.firstName} {client.lastName}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Intervenant */}
                    <div className="space-y-2">
                        <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
                            <User className="mr-2 text-blue-500" size={16} />
                            Intervenant assigné
                        </label>
                        <select
                            value={formData.employeeId || 'unassigned'}
                            onChange={(e) => {
                                const newVal = e.target.value === 'unassigned' ? null : e.target.value;
                                handleChange('employeeId', newVal);
                                // Auto-update status logic handled by logic outside or explicit set? 
                                // DayView logic does not seem to auto-set status here, but usually recommended.
                                // We'll keep it simple: just update update ID. logic elsewhere.
                            }}
                            className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        >
                            <option value="unassigned">Non assigné</option>
                            {employees.map(emp => (
                                <option key={emp.userId} value={emp.userId}>
                                    {emp.name || `${emp.firstName} ${emp.lastName}`}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Statut */}
                    <div className="space-y-2">
                        <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
                            <AlertCircle className="mr-2 text-blue-500" size={16} />
                            Statut du rendez-vous
                        </label>
                        <select
                            value={formData.status}
                            onChange={(e) => handleChange('status', e.target.value)}
                            className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        >
                            <option value="libre">Libre</option>
                            <option value="reserve">En attente de validation</option>
                            <option value="assigne">Assigné</option>
                            <option value="in_progress">En cours</option>
                            <option value="completed">Terminé</option>
                        </select>
                    </div>

                    {/* Section Récurrence */}
                    {formData.recurrenceSeriesId && (
                        <div className="space-y-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                            <div className="flex items-center gap-2 mb-3">
                                <RotateCcw size={18} className="text-blue-600 dark:text-blue-400" />
                                <h4 className="font-semibold text-blue-900 dark:text-blue-200">Paramètres de récurrence</h4>
                            </div>

                            {/* Type de récurrence */}
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Type de récurrence</label>
                                <select
                                    value={formData.recurrenceBase || 'weekly'}
                                    onChange={(e) => handleChange('recurrenceBase', e.target.value)}
                                    className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                >
                                    <option value="daily">Quotidien</option>
                                    <option value="weekly">Hebdomadaire</option>
                                    <option value="monthly">Mensuel</option>
                                </select>
                            </div>

                            {/* Fréquence */}
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Répéter tous les {formData.recurrenceBase === 'daily' ? 'jours' : formData.recurrenceBase === 'weekly' ? 'semaines' : 'mois'}
                                </label>
                                <input
                                    type="number"
                                    min="1"
                                    max="52"
                                    value={formData.recurrenceFrequency || 1}
                                    onChange={(e) => handleChange('recurrenceFrequency', parseInt(e.target.value) || 1)}
                                    className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                />
                            </div>

                            {/* Jours de la semaine (si hebdomadaire) */}
                            {formData.recurrenceBase === 'weekly' && (
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Jours de la semaine</label>
                                    <div className="flex flex-wrap gap-2">
                                        {['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'].map((day, index) => (
                                            <button
                                                key={index}
                                                type="button"
                                                onClick={() => toggleRecurrenceDay(index)}
                                                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${(formData.recurrenceDay || []).includes(index)
                                                    ? 'bg-blue-500 text-white'
                                                    : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                                                    }`}
                                            >
                                                {day}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Type de fin de récurrence */}
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Fin de la récurrence</label>
                                <select
                                    value={formData.recurrenceEndType || 'never'}
                                    onChange={(e) => handleChange('recurrenceEndType', e.target.value)}
                                    className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                >
                                    <option value="never">Jamais</option>
                                    <option value="after">Après un nombre d'occurrences</option>
                                    <option value="on">À une date spécifique</option>
                                </select>
                            </div>

                            {/* Nombre d'occurrences (si type = after) */}
                            {formData.recurrenceEndType === 'after' && (
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Nombre d'occurrences</label>
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm text-gray-600 dark:text-gray-400">Après</span>
                                        <input
                                            type="number"
                                            min="1"
                                            max="100"
                                            value={formData.recurrenceEndAfter || 1}
                                            onChange={(e) => handleChange('recurrenceEndAfter', parseInt(e.target.value) || 1)}
                                            className="w-20 p-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                        />
                                        <span className="text-sm text-gray-600 dark:text-gray-400">occurrences</span>
                                    </div>
                                </div>
                            )}

                            {/* Date de fin (si type = on) */}
                            {formData.recurrenceEndType === 'on' && (
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Date de fin de récurrence</label>
                                    <input
                                        type="date"
                                        value={formData.recurrenceEndDate instanceof Date && !isNaN(formData.recurrenceEndDate)
                                            ? formData.recurrenceEndDate.toISOString().split('T')[0]
                                            : formData.recurrenceEndDate || ''}
                                        onChange={(e) => handleChange('recurrenceEndDate', e.target.value)}
                                        className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                    />
                                </div>
                            )}

                            <div className="text-sm text-blue-700 dark:text-blue-300 bg-blue-100 dark:bg-blue-900/30 p-3 rounded-lg">
                                ⚠️ Les modifications de récurrence s'appliqueront à cette occurrence et toutes les occurrences futures.
                            </div>
                        </div>
                    )}
                </div>
            </ModalBody>
            <ModalFooter>
                <div className="flex w-full justify-between">
                    {canDelete && (
                        <button
                            type="button"
                            onClick={handleDelete}
                            className="flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 dark:bg-red-900/30 dark:text-red-400 dark:hover:bg-red-900/50"
                        >
                            <Trash2 size={16} className="mr-2" />
                            Supprimer
                        </button>
                    )}
                    <div className="flex gap-2 ml-auto">
                        <ModalButton variant="secondary" onClick={onClose}>
                            Annuler
                        </ModalButton>
                        <ModalButton variant="primary" onClick={handleSave}>
                            Mettre à jour
                        </ModalButton>
                    </div>
                </div>
            </ModalFooter>
        </EnhancedModal>
    );
};

export default EnhancedEditAppointmentModal;
